export interface BbPoint {
  upper: number;
  middle: number;
  lower: number;
}

/**
 * Wilder's RSI on a closing-price series. Matches TradingView's built-in RSI
 * (which uses RMA / Wilder's smoothing). First (period) entries are null.
 */
export function rsi(closes: number[], period: number = 14): (number | null)[] {
  const n = closes.length;
  const out: (number | null)[] = new Array(n).fill(null);
  if (n <= period) return out;

  let avgGain = 0;
  let avgLoss = 0;
  // Seed: simple average of gains/losses over the first `period` changes.
  for (let i = 1; i <= period; i++) {
    const diff = closes[i] - closes[i - 1];
    if (diff > 0) avgGain += diff;
    else avgLoss -= diff;
  }
  avgGain /= period;
  avgLoss /= period;
  out[period] = rsiFromAverages(avgGain, avgLoss);

  for (let i = period + 1; i < n; i++) {
    const diff = closes[i] - closes[i - 1];
    const gain = diff > 0 ? diff : 0;
    const loss = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    out[i] = rsiFromAverages(avgGain, avgLoss);
  }
  return out;
}

function rsiFromAverages(avgGain: number, avgLoss: number): number {
  if (avgLoss === 0) return 100;
  if (avgGain === 0) return 0;
  const rs = avgGain / avgLoss;
  return 100 - 100 / (1 + rs);
}

/**
 * Bollinger Bands over a numeric series. Uses population standard deviation
 * (divide by period), matching TradingView / most charting conventions.
 * Returns null for the first (period - 1) entries where there isn't enough
 * data to compute a window.
 */
export function bollingerBands(
  values: number[],
  period: number,
  mult: number
): (BbPoint | null)[] {
  const out: (BbPoint | null)[] = new Array(values.length).fill(null);
  if (period <= 0 || values.length < period) return out;

  let sum = 0;
  let sumSq = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    sum += v;
    sumSq += v * v;
    if (i >= period) {
      const drop = values[i - period];
      sum -= drop;
      sumSq -= drop * drop;
    }
    if (i >= period - 1) {
      const mean = sum / period;
      // Population variance; clamp at 0 to guard against floating-point drift.
      const variance = Math.max(0, sumSq / period - mean * mean);
      const std = Math.sqrt(variance);
      out[i] = {
        middle: mean,
        upper: mean + mult * std,
        lower: mean - mult * std,
      };
    }
  }
  return out;
}
